
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import FabricService, {
  SwatchUploadResult,
  TextureUploadResult,
} from "@/services/fabric_service";
import { swatchUrl, textureUrl } from "@/services/api";
import { downscaleForUpload } from "@/services/image_downscale";

/** What commit() hands back to EditView so it can refresh the fabric doc. */
export interface AssetCommitResult {
  kind: "swatch" | "texture";
  image?: string;
  texture_key?: string;
  cacheBust?: string | number;
}

/** Clockwise quarter-turns. Mirrors RotateDeg in the API's asset service. */
type RotateDeg = 0 | 90 | 180 | 270;

/**
 * One asset block (swatch or texture) on the edit page.
 *
 * This component STAGES, it does not commit. Picking a file, ticking "Copy Swatch
 * Image", and rotating all just move local state and update the preview; the
 * parent's Save button calls commit() to actually write anything. That is
 * deliberate: the rest of the edit page has always been staged-until-Save, and
 * having the asset controls fire immediately made them the odd ones out.
 *
 * It also makes rotation cheap. Rotation is a lossy JPEG re-encode server-side, so
 * the old fire-on-click button spent one generation of quality PER CLICK. Staging
 * accumulates the angle and sends it once — and if the user lands back on 0°, it
 * sends nothing at all.
 */
@Component
export default class FabricImageUpload extends Vue {
  // "swatch" → POST /fabrics/:id/swatch ; "texture" → POST /fabrics/:id/texture
  @Prop({ required: true }) readonly kind!: "swatch" | "texture";
  @Prop({ required: true }) readonly fabricId!: string;
  @Prop({ default: "" }) readonly textureKey!: string;
  // Current image filename — shown read-only in the swatch block. Server-computed.
  @Prop({ default: "" }) readonly image!: string;
  // Whether the asset FILES actually exist on Wasabi (HEAD-probed by the API via
  // GET /fabrics/:id/assets and passed down by EditView). Distinct from having a
  // texture_key, which is derived from fabric data and exists before any bytes
  // are written — so it cannot tell us whether there's a real texture to protect.
  @Prop({ default: false }) readonly swatchExists!: boolean;
  @Prop({ default: false }) readonly textureExists!: boolean;
  // fabric.assets_updated_at — the ?v= token for the stored image. Changes exactly
  // when the bytes do, so the CDN copy stays cacheable in between.
  @Prop({ default: "" }) readonly assetBust!: string | number;

  private fabricService = new FabricService();

  // ~60% larger than the previous 120px preview.
  protected previewSize = 192;

  protected pendingFile: File | null = null;
  protected localPreview = "";
  protected uploading = false;
  // True while a picked file is being decoded + downscaled, before it is staged.
  protected preparing = false;
  protected progress = 0;
  // Accumulated clockwise rotation to apply on the next commit. 0 = leave alone.
  protected pendingRotation: RotateDeg = 0;
  // Whether a swatch upload should also overwrite the 3D texture.
  //
  // Defaults to ON only when there is NO existing texture to destroy. It used to
  // be hardcoded `true`, which meant re-uploading a swatch silently clobbered a
  // hand-authored seamless texture unless the user remembered to untick it. The
  // safe default is: seed the texture when there isn't one, never overwrite one
  // that exists without an explicit tick. Set reactively in onAssetsResolved()
  // because the probe result arrives after this component mounts.
  protected copyToTextures = false;
  // Texture block only: use the fabric's existing swatch as the texture instead
  // of uploading a file.
  protected copyFromSwatch = false;
  // Cache-bust token for the CDN preview, set from a commit response. Overrides
  // the assetBust prop until the parent reloads the fabric.
  protected bust: string | number = "";

  $refs!: any;

  /**
   * The probe result lands asynchronously (and can change after an upload), so
   * the default is derived in a watcher rather than at field-init time.
   * `immediate` covers the case where the parent already knew the answer.
   *
   * Only re-derive while the user hasn't staged anything — otherwise a late
   * probe response would yank the checkbox out from under someone who had
   * already made a deliberate choice about the file they just picked.
   */
  @Watch("textureExists", { immediate: true })
  protected onAssetsResolved(): void {
    if (this.kind !== "swatch" || this.pendingFile) return;
    this.copyToTextures = !this.textureExists;
  }

  get swatchAvailable(): boolean {
    return this.swatchExists;
  }

  // Is there a stored asset for THIS block to act on?
  get assetPresent(): boolean {
    return this.kind === "swatch" ? this.swatchExists : this.textureExists;
  }

  /**
   * Rotation needs SOMETHING to rotate — but that something can be staged rather
   * than stored. A file you just picked, or a swatch you've staged as the texture,
   * can both be rotated before they are ever uploaded (the API applies the angle
   * inside the encode it was going to run anyway, at no extra cost).
   */
  get canRotate(): boolean {
    return this.assetPresent || !!this.pendingFile || this.copyFromSwatch;
  }

  // Show the "Copy Swatch Image" toggle in the texture block only.
  get showCopySwatch(): boolean {
    return this.kind === "texture";
  }

  get hasPendingChanges(): boolean {
    return !!this.pendingFile || this.copyFromSwatch || this.pendingRotation !== 0;
  }

  get pendingSummary(): string {
    const bits: string[] = [];
    if (this.pendingFile) bits.push("a new image");
    else if (this.copyFromSwatch) bits.push("the swatch copied over");
    if (this.pendingRotation) bits.push(`a ${this.pendingRotation}° clockwise rotation`);
    if (!bits.length) return "";
    const what = bits.length === 2 ? `${bits[0]} and ${bits[1]}` : bits[0];
    return `Staged: ${what}.`;
  }

  @Watch("hasPendingChanges")
  protected onPendingChange(pending: boolean): void {
    this.$emit("pending-change", { kind: this.kind, pending });
  }

  get title(): string {
    return this.kind === "swatch" ? "Swatch Image" : "3D Texture (seamless repeat)";
  }

  get description(): string {
    return this.kind === "swatch"
      ? "The catalog swatch. By default this also seeds the 3D texture."
      : "Independent seamless texture used on the 3D canopy. Review the tiled preview before saving.";
  }

  get tiled(): boolean {
    return this.kind === "texture";
  }

  get showCopyToTextures(): boolean {
    return this.kind === "swatch";
  }

  get fileName(): string {
    return this.pendingFile ? this.pendingFile.name : "";
  }

  // Read-only, server-computed identifier shown inside each asset block:
  // the swatch block shows the image filename; the texture block shows the
  // texture_key. Neither is editable (both are derived API-side).
  get computedLabel(): string {
    return this.kind === "swatch" ? "Image Filename (computed)" : "Texture Key (computed)";
  }

  get computedValue(): string {
    if (this.kind === "swatch") return this.image || "— none —";
    return this.textureKey || "— set on first swatch upload —";
  }

  get placeholder(): string {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require("@/assets/frankford_logo_black.svg");
  }

  /** A commit response's token wins; otherwise use the fabric's asset clock. */
  get activeBust(): string | number {
    return this.bust || this.assetBust || "";
  }

  // Local preview (just-picked file) wins over the current CDN image.
  get previewSrc(): string {
    if (this.localPreview) return this.localPreview;
    if (!this.textureKey) return "";
    // "Copy Swatch Image" staged: preview the SWATCH tiled, so the user sees how
    // it will actually repeat on the canopy before committing to it. A swatch is
    // not necessarily seamless, and that is exactly what this preview reveals.
    if (this.kind === "texture" && this.copyFromSwatch) {
      return swatchUrl(this.textureKey, this.activeBust);
    }
    return this.kind === "swatch"
      ? swatchUrl(this.textureKey, this.activeBust)
      : textureUrl(this.textureKey, this.activeBust);
  }

  get previewTransform(): Record<string, string> {
    return this.pendingRotation
      ? { transform: `rotate(${this.pendingRotation}deg)` }
      : {};
  }

  /** The 3 x 3 frame. Cells are square, so a rotated cell still fills it. */
  get tileGridStyle(): Record<string, string> {
    const box = this.previewSize;
    return {
      width: `${box}px`,
      height: `${box}px`,
      display: "grid",
      gridTemplateColumns: `repeat(3, ${box / 3}px)`,
      gridTemplateRows: `repeat(3, ${box / 3}px)`,
      border: "1px solid #e5e5e5",
      overflow: "hidden",
    };
  }

  get tileCellStyle(): Record<string, string> {
    const src = this.previewSrc;
    return {
      backgroundImage: src ? `url('${src}')` : "none",
      // Stretch each tile to fill its square cell — the same thing the old
      // background-size did, kept so the preview doesn't change shape.
      backgroundSize: "100% 100%",
      ...this.previewTransform,
    };
  }

  @Watch("textureKey")
  onKeyChange(): void {
    // New server-computed key — drop any stale local preview.
    this.localPreview = "";
  }

  /**
   * Stage the picked file, downscaling it first.
   *
   * The file is shrunk HERE rather than at commit-time so that what we preview is
   * what we upload, and so the (brief) decode cost is paid while the user is still
   * reading the form instead of appearing as a stall after they press Save.
   */
  protected async onPick(): Promise<void> {
    const picked = this.$refs.file.files?.[0] || null;
    if (this.localPreview) URL.revokeObjectURL(this.localPreview);
    this.localPreview = "";
    this.pendingFile = null;

    if (!picked) return;

    this.preparing = true;
    try {
      const prepared = await downscaleForUpload(picked);
      this.pendingFile = prepared;
      this.localPreview = URL.createObjectURL(prepared);
    } finally {
      this.preparing = false;
    }
  }

  /** Stage another quarter-turn. Four clicks wrap back to 0 — and commit nothing. */
  protected rotate(): void {
    this.pendingRotation = (((this.pendingRotation + 90) % 360) as RotateDeg);
  }

  /** Drop every staged change in this block and go back to what's on the server. */
  protected reset(): void {
    this.pendingFile = null;
    this.copyFromSwatch = false;
    this.pendingRotation = 0;
    if (this.localPreview) URL.revokeObjectURL(this.localPreview);
    this.localPreview = "";
    if (this.$refs.file) this.$refs.file.value = "";
  }

  // Picking a file and copying the swatch are mutually exclusive sources for the
  // texture. Toggling the copy on discards any staged file so the preview and
  // the commit can't disagree about what is about to be applied.
  @Watch("copyFromSwatch")
  protected onCopyFromSwatchChange(on: boolean): void {
    if (!on) return;
    this.pendingFile = null;
    if (this.localPreview) URL.revokeObjectURL(this.localPreview);
    this.localPreview = "";
    if (this.$refs.file) this.$refs.file.value = "";
  }

  /**
   * Write this block's staged changes. Called by EditView's Save — this is the
   * ONLY method here that talks to the API.
   *
   * Collapses to exactly one request. A staged file carries its rotation with it
   * (the server folds the angle into the encode it already performs), so a
   * new-image-plus-rotation costs one upload and zero extra re-encodes. Rotation
   * on its own is the only case that hits the dedicated rotate endpoint, and it
   * sends the ACCUMULATED angle — one re-encode no matter how many times the
   * button was pressed.
   *
   * Throws on failure so Save can abort before touching fabric metadata.
   */
  public async commit(): Promise<AssetCommitResult | null> {
    if (!this.hasPendingChanges || !this.fabricId) return null;

    this.uploading = true;
    this.progress = 0;
    try {
      let result: AssetCommitResult;

      if (this.copyFromSwatch) {
        const res: TextureUploadResult = await this.fabricService.copyTextureFromSwatch(
          this.fabricId,
          this.pendingRotation
        );
        result = { kind: "texture", ...res };
      } else if (this.pendingFile) {
        const file = this.pendingFile;
        const onProgress = (p: number) => (this.progress = p);
        if (this.kind === "swatch") {
          const res: SwatchUploadResult = await this.fabricService.uploadSwatch(
            this.fabricId,
            file,
            this.copyToTextures,
            this.pendingRotation,
            onProgress
          );
          result = { kind: "swatch", ...res };
        } else {
          const res: TextureUploadResult = await this.fabricService.uploadTexture(
            this.fabricId,
            file,
            this.pendingRotation,
            onProgress
          );
          result = { kind: "texture", ...res };
        }
      } else {
        // Rotation only, against the stored asset.
        const res = await this.fabricService.rotateAsset(
          this.fabricId,
          this.kind,
          this.pendingRotation as 90 | 180 | 270
        );
        result = { kind: this.kind, texture_key: res.texture_key, cacheBust: res.cacheBust };
      }

      this.bust = result.cacheBust || Date.now();
      this.reset();
      return result;
    } finally {
      this.uploading = false;
    }
  }
}
